import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { CMS_BASE_URL } from '@/utils/constants';
import axios from 'axios';
import merge from 'lodash/merge';
import React, { useCallback } from 'react';

interface SearchEngineServiceProps {
  data: any;
  engine: string;
  isAuthenticated: boolean;
  config?: any;
  urlPath: string;
}

interface UseSearchEngineProps {
  engine: string;
  authEngine: string;
}

interface RetrieveSearchEngineDataProps {
  data: any;
  searchToken: string;
}

const PUBLIC_SEARCH_API =
  'https://e9086f645f4348188a99892a36b61d30.ent-search.us-east-1.aws.cloud.es.io/api/as/v1/engines';
const AUTHENTICATED_SEARCH_API = `${CMS_BASE_URL}/restservices/portal/engines`;

export const searchEngineService = ({
  data,
  engine,
  isAuthenticated = false,
  config = {},
  urlPath = '',
}: SearchEngineServiceProps) => {
  const baseUrl = isAuthenticated ? AUTHENTICATED_SEARCH_API : PUBLIC_SEARCH_API;
  const baseConfig: any = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };
  return axios.post(`${baseUrl}/${engine}${urlPath}`, data, merge(baseConfig, config));
};

export const useSearchEngine = ({ engine, authEngine }: UseSearchEngineProps) => {
  const { isAuthenticated } = useIsAuthenticated();

  const selectedEngine = React.useMemo(
    () => (isAuthenticated ? authEngine : engine),
    [isAuthenticated, authEngine, engine],
  );

  const retrieveSearchEngineData = useCallback(
    async ({ data: payload, searchToken }: RetrieveSearchEngineDataProps) => {
      const requestHeaders: any = {};

      /**
       * If the user is not authenticated, use the search token
       */
      if (!isAuthenticated) {
        requestHeaders.Authorization = `Bearer ${searchToken}`;
      }

      const { data } = await searchEngineService({
        engine: selectedEngine,
        data: payload,
        config: {
          headers: requestHeaders,
        },
        isAuthenticated,
        urlPath: '/search',
      });
      return data;
    },
    [selectedEngine, isAuthenticated],
  );

  return {
    retrieveSearchEngineData,
  };
};
